import type { MemberTimeItem } from 'lowcode/utils/context/memberTimeZone';
import dayjs from './dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/de';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/it';
import 'dayjs/locale/ja';
import 'dayjs/locale/ko';

const regex2 = /UTC([+-]?\d{1,2})/;

const regex1 = /UTC(.*)/;

const localeObj: any = {
    jp: 'ja',
    kr: 'ko',
    de: 'de',
    fr: 'fr',
    it: 'it',
    es: 'es',
};

// GMT 基于 格林尼治标准时间 使用 符号方向与 UTC 一致 的表示法
const GMT_prefix = 'GMT';

const FORMAT_STRING = 'YYYY-MM-DD HH:mm:ss';

const FORMAT_STRING2 = 'MMMM D, YYYY';

export const BEIJING_LOCAL = 'UTC+8:00';

const SouthernCountrys = ['au'];

/**
 * 时区格式化 UTC
 * @param utcFormat utc的时区信息参数
 * @returns 返回UTC的偏移量 或者 null
 */
export const formatUtcTimeZone = (utcFormat: string) => {
    // 不存在参数的话返回 空
    if (!utcFormat.startsWith('UTC')) {
        return null;
    }

    const utcOffsetString = utcFormat.substring(3);
    if (utcOffsetString === ':00' || utcOffsetString === '00:00') {
        return `+00:00`;
    }
    return utcOffsetString;
};

/**
 * 时区格式化 GMT
 * @param utcFormat utc的时区信息参数
 * @returns 返回GMT时区格式 或者 null
 */
export const formatGmtTimeZone = (utcFormat: string) => {
    // 不存在参数的话返回 空
    if (!utcFormat.startsWith('UTC')) {
        return null;
    }
    const utcOffsetString = utcFormat.substring(3);
    if (utcOffsetString === ':00' || utcOffsetString === '00:00') {
        return `${GMT_prefix}+00:00`;
    }
    return `${GMT_prefix}${utcOffsetString}`;
};

/**
 * utc偏移量
 * @param utcFormat utc的时区信息参数
 * @returns utcoffset
 */
export const getUtcOffset = (utcFormat: string) => {
    const offsetStr = formatUtcTimeZone(utcFormat);
    if (offsetStr) {
        const timerArr = offsetStr.split(':');

        if (parseInt(timerArr[0], 10) >= 0) {
            return parseInt(timerArr[0], 10) * 60 + parseInt(timerArr[1], 10);
        }

        return parseInt(timerArr[0], 10) * 60 - parseInt(timerArr[1], 10);
    }
    return 0;
};

// 获取当前传入时间的当前处于的时区
export const getTheTimeCurrentTimeZone = (dateInfo: any, timezoneList: MemberTimeItem[]) => {
    const beijingDateInfo = dateInfo.utcOffset(getUtcOffset(BEIJING_LOCAL || ''));
    const theYear = beijingDateInfo.year();
    const timestamp1 = beijingDateInfo.valueOf();
    let currentTimeZoneInfo = timezoneList?.find((item: MemberTimeItem) => item.year === theYear);
    if (Array.isArray(timezoneList) && timezoneList.length > 0 && !currentTimeZoneInfo) {
        timezoneList.sort((a, b) => b.year - a.year);

        const lastTimeZoneInfo = timezoneList[0];
        const lastYear = lastTimeZoneInfo.year;
        if (!lastTimeZoneInfo.needSwitchFlag) {
            return lastTimeZoneInfo.commonTimezone;
        }
        const difference = theYear - lastYear;
        const nextSwitchSummerTimestamp = dayjs.utc(Number(lastTimeZoneInfo.switchSummerTimestamp)).add(difference, 'years').valueOf();
        const nextSwitchWinterTimestamp = dayjs.utc(Number(lastTimeZoneInfo.switchWinterTimestamp)).add(difference, 'years').valueOf();
        currentTimeZoneInfo = {
            ...lastTimeZoneInfo,
            year: theYear,
            switchSummerTimestamp: nextSwitchSummerTimestamp,
            switchWinterTimestamp: nextSwitchWinterTimestamp,
        };
    }
    if (currentTimeZoneInfo && currentTimeZoneInfo.enable) {
        if (!currentTimeZoneInfo.needSwitchFlag) {
            return currentTimeZoneInfo.commonTimezone;
        }

        if (!SouthernCountrys.includes(currentTimeZoneInfo.site.toLocaleLowerCase())) {
            if (currentTimeZoneInfo.switchSummerTimestamp <= timestamp1 && timestamp1 < currentTimeZoneInfo.switchWinterTimestamp) {
                return currentTimeZoneInfo.summerTimezone;
            } else {
                return currentTimeZoneInfo.winterTimezone;
            }
        }
        if (SouthernCountrys.includes(currentTimeZoneInfo.site.toLocaleLowerCase())) {
            if (currentTimeZoneInfo.switchWinterTimestamp <= timestamp1 && timestamp1 < currentTimeZoneInfo.switchSummerTimestamp) {
                return currentTimeZoneInfo.winterTimezone;
            } else {
                return currentTimeZoneInfo.summerTimezone;
            }
        }
    }
    return BEIJING_LOCAL;
};

/**
 * 当地时间格式化
 * @param timestamp 时间戳
 * @param format 格式化字符串
 * @returns dateString
 */
export const formatLocalTime = (timestamp: string | number, timezoneList: MemberTimeItem[], format: string = FORMAT_STRING) => {
    if (!timestamp) {
        return '';
    }

    if (Array.isArray(timezoneList) && timezoneList.length === 0) {
        return dayjs(Number(timestamp)).format(format);
    }
    const dateInfo = dayjs.utc(Number(timestamp));

    const currentTimeZone = getTheTimeCurrentTimeZone(dateInfo, timezoneList);

    // 传入了时区，设置上时区，否则本地时间
    const dateObj = dateInfo.utcOffset(getUtcOffset(currentTimeZone || ''));

    return dateObj.format(format);
};

/**
 * 不同时区时间转换
 * @param timeStr 时间字符串
 * @param localZone 时间字符串对应的时区
 * @param targetZone 需要转换的时区
 * @returns dateString
 */
export const formatTargetLocalTime = (
    timeStr: string,
    timezoneList: MemberTimeItem[],
    localZone: string = BEIJING_LOCAL,
    locale?: string,
    formatLocal?: boolean,
) => {
    if (!timeStr) {
        return '';
    }
    if (formatLocal && locale) {
        const localeString = localeObj[locale] || 'en';
        dayjs.locale(localeString);
    }
    const dateInfo1 = dayjs(timeStr).utcOffset(0); // 先创建成utc对象

    const currentTimeZone = getTheTimeCurrentTimeZone(dateInfo1, timezoneList);

    const dateInfo = dayjs(timeStr).utcOffset(getUtcOffset(localZone)).utcOffset(getUtcOffset(currentTimeZone));
    return dateInfo.format(formatLocal ? FORMAT_STRING2 : FORMAT_STRING);
};

export const getDateToLocalString = (timestamp: string | number, locale: string, timezoneList: MemberTimeItem[], format: string = FORMAT_STRING) => {
    const localeString = localeObj[locale] || 'en';
    // dayjs.locale(localeString);
    if (!timestamp) {
        return '';
    }

    if (Array.isArray(timezoneList) && timezoneList.length === 0) {
        return dayjs(Number(timestamp)).format(format);
    }

    const dateInfo = dayjs.utc(Number(timestamp));
    const currentTimeZone = getTheTimeCurrentTimeZone(dateInfo, timezoneList);

    // 传入了时区，设置上时区，否则本地时间
    const dateObj = dateInfo.utcOffset(getUtcOffset(currentTimeZone || ''));
    return dateObj.format(format);
};
